import { CombinedGraphQLErrors } from '@apollo/client';
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Location, useLocation, useNavigate } from 'react-router-dom';

import { homeAudiencesPath, homeOrganizationsPath, homePath, isDetailsPagePath, visitIQCurrentOrg } from '~/constants';
import { OrganizationType, useValidateOrganizationLazyQuery } from '~/types/api.graphql';

import { useDirtyFormContext } from './DirtyFormContext';

export interface CurrentOrg {
  id: string;
  name: string;
  imageUrl?: string;
  organizationType: OrganizationType;
}

interface CurrentOrgContextProps {
  activeOrg: CurrentOrg | null;
  switchOrg: (org: CurrentOrg | null) => void;
  resetActiveOrg: () => void;
}

const defaultValues: CurrentOrgContextProps = {
  activeOrg: null,
  switchOrg: null,
  resetActiveOrg: null,
};

const CurrentOrgContext = createContext<CurrentOrgContextProps>(defaultValues);

interface CurrentOrgProviderProps {
  children: ReactNode;
}

export const CurrentOrgProvider: FC<CurrentOrgProviderProps> = ({ children }) => {
  const { confirmIfDirty } = useDirtyFormContext();
  const navigate = useNavigate();
  const location = useLocation();
  // performSwitch may run long after render (deferred behind confirmIfDirty's unsaved-changes modal,
  // or reused across same-pathname/different-state navigations that don't recreate the callback), so
  // it must read the latest location rather than close over a stale one.
  const locationRef = useRef<Location>(location);
  locationRef.current = location;

  const [activeOrg, setActiveOrgState] = useState<CurrentOrg | null>(() => {
    try {
      const saved = sessionStorage.getItem(visitIQCurrentOrg);
      return saved ? (JSON.parse(saved) as CurrentOrg) : null;
    } catch (error) {
      console.error('Failed to parse active org from sessionStorage', error);
      return null;
    }
  });

  const persistOrg = useCallback((org: CurrentOrg | null) => {
    setActiveOrgState(org);
    if (org) {
      sessionStorage.setItem(visitIQCurrentOrg, JSON.stringify(org));
    } else {
      sessionStorage.removeItem(visitIQCurrentOrg);
    }
  }, []);

  const resetActiveOrg = useCallback(() => persistOrg(null), [persistOrg]);

  const [validateOrganization] = useValidateOrganizationLazyQuery({
    fetchPolicy: 'network-only',
    // errorPolicy 'all' makes the execute promise resolve with `{ error }` instead of rejecting
    // (Apollo v4 default 'none' rejects), so the clear logic below actually runs.
    errorPolicy: 'all',
  });

  // Validate only the org hydrated from localStorage at mount; switching orgs via the selector
  // must not re-trigger validation.
  useEffect(() => {
    if (!activeOrg) return;

    // errorHandledLocally suppresses the global FORBIDDEN handler (toast + forbidden-page redirect)
    // for this probe: a stale-org failure is handled here by clearing the selection, otherwise it
    // would feed the FORBIDDEN -> navigate(home) -> refetch loop.
    validateOrganization({ variables: { organizationId: activeOrg.id }, context: { errorHandledLocally: true } })
      .then(({ data, error }) => {
        // Only clear on a definitive server rejection (e.g. ForbiddenError when the user lost
        // access to the org), or a successful response with no org. A pure network error is
        // transient (data is empty but no GraphQL error) and must not wipe the user's selection.
        if (error) {
          if (CombinedGraphQLErrors.is(error)) persistOrg(null);
          return;
        }
        if (!data?.organization) persistOrg(null);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const performSwitch = useCallback(
    (org: CurrentOrg | null, wasDirty: boolean) => {
      persistOrg(org);
      const { pathname } = locationRef.current;
      if (wasDirty) {
        navigate(homePath, { replace: true });
      } else if (isDetailsPagePath(pathname)) {
        const targetPath = pathname.startsWith(homeOrganizationsPath) ? homeOrganizationsPath : homeAudiencesPath;
        navigate(targetPath, { replace: true });
      }
      // An org-list level (All / White Label / Agency / Customer) that the new org's subtree can't
      // contain is handled by OrganizationsList itself, which redirects to "All organizations".
    },
    [navigate, persistOrg],
  );

  const switchOrg = useCallback(
    (org: CurrentOrg | null) => {
      if (org?.id === activeOrg?.id) return;
      confirmIfDirty((wasDirty) => performSwitch(org, wasDirty));
    },
    [activeOrg, confirmIfDirty, performSwitch],
  );

  const value = useMemo(() => ({ activeOrg, switchOrg, resetActiveOrg }), [activeOrg, switchOrg, resetActiveOrg]);

  return <CurrentOrgContext.Provider value={value}>{children}</CurrentOrgContext.Provider>;
};

export const useCurrentOrgContext: () => CurrentOrgContextProps = () => {
  return useContext(CurrentOrgContext);
};
